// These polyfills make stable language features of modern ECMAScript
// available accross all target browsers. We include IE11 as it is
// currently still functionally supported for broader web projects
// such as www.ag.ch. Remove or limit these polyfills to reduce build
// if your project allows.
import 'react-app-polyfill/stable';
import 'react-app-polyfill/ie11';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { parseProps, parsePropsTypes } from '@ktag/styleguide/dist/util';
import App from './App';

// IE11 cannot forEach over nodelists
Array.prototype.forEach.call(
  document.querySelectorAll(`[data-${process.env.REACT_APP_NAME}]`),
  (element: HTMLDivElement) => {
    // prevent multiple initializations
    if (element.dataset.initialized) return;
    // eslint-disable-next-line no-param-reassign
    element.dataset.initialized = 'initialized';

    // read and parse all specified data attributes to
    // ensure their types are correct since data attributes
    // otherwise are always of type string
    const parsedProps = parseProps(element.dataset, [
      { key: 'standalone', type: parsePropsTypes.bool },
      { key: 'mode', type: parsePropsTypes.string },
      { key: 'config', type: parsePropsTypes.json },
      { key: 'apiUrl', type: parsePropsTypes.string, datasetKey: 'api' },
      {
        key: 'translationApiUrl',
        type: parsePropsTypes.string,
        datasetKey: 'translationApi',
      },
      {
        key: 'translationMissingApiUrl',
        type: parsePropsTypes.string,
        datasetKey: 'translationMissingApi',
      },
      { key: 'manageBaseUrl', type: parsePropsTypes.string },
      { key: 'overviewBaseUrl', type: parsePropsTypes.string },
      { key: 'viewerBaseUrl', type: parsePropsTypes.string },
      { key: 'latestId', type: parsePropsTypes.int },
      { key: 'latestCount', type: parsePropsTypes.int },
      { key: 'actionsShowSubscribe', type: parsePropsTypes.bool },
      { key: 'actionsShowEdit', type: parsePropsTypes.bool },
      { key: 'actionsShowUnsubscribe', type: parsePropsTypes.bool },
      { key: 'actionsId', type: parsePropsTypes.int },
      { key: 'actionsOrigin', type: parsePropsTypes.string },
      { key: 'siteKey', type: parsePropsTypes.string, datasetKey: 'captchaSitekey' },
    ]);

    // Enforce shadow dom for testing purposes. But consider that in
    // this scenario the proxima nova webfonts will not be loaded.
    // if (element.dataset.standalone === 'true' && !element.shadowRoot) {
    //   document.documentElement.style.fontSize = '62.5%';
    //   element.attachShadow({ mode: 'open' });
    // }

    // combine all props from data attributes with the
    // root element itself. The element can be used inside
    // the app to determine where in the document it is located
    // and if it's running an a shadow DOM
    const props = {
      rootElement: element,
      ...(typeof parsedProps === 'object' ? parsedProps : {}),
    };

    const root = element.shadowRoot
      ? createRoot(element.shadowRoot)
      : createRoot(element);

    root.render(React.createElement(App, props));
  },
);
